import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  getBibleStyle,
  listBibleCharacters,
  listBibleCostumes,
  listBibleLocations,
  listBibleProps,
  listShots,
} from '@/api/studio-production'
import {
  assignmentItemKey,
  parseShotSceneKey,
  type AssignmentItemLabel,
} from '@/features/studio/lib/assignment-display'

export function useAssignmentItemLabels(productionId: string) {
  const { t } = useTranslation()
  const [labels, setLabels] = useState<Map<string, AssignmentItemLabel>>(new Map())
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!productionId) {
      setLabels(new Map())
      return
    }
    let cancelled = false
    setLoading(true)
    void (async () => {
      try {
        const [chars, locs, props, costumes, style, shots] = await Promise.all([
          listBibleCharacters(productionId, { limit: 0 }).catch(() => ({ items: [] })),
          listBibleLocations(productionId, { limit: 0 }).catch(() => ({ items: [] })),
          listBibleProps(productionId, { limit: 0 }).catch(() => ({ items: [] })),
          listBibleCostumes(productionId, { limit: 0 }).catch(() => ({ items: [] })),
          getBibleStyle(productionId).catch(() => null),
          listShots(productionId, { limit: 500 }).catch(() => ({ items: [] })),
        ])
        if (cancelled) return
        const map = new Map<string, AssignmentItemLabel>()
        const kind = (key: string) => t(key)
        for (const c of chars.items) {
          map.set(assignmentItemKey('bible_character', c.public_id), {
            kindLabel: kind('bible.kindCharacter'),
            title: c.name,
          })
        }
        for (const l of locs.items) {
          map.set(assignmentItemKey('bible_location', l.public_id), {
            kindLabel: kind('bible.kindLocation'),
            title: l.name,
          })
        }
        for (const p of props.items) {
          map.set(assignmentItemKey('bible_prop', p.public_id), {
            kindLabel: kind('bible.kindProp'),
            title: p.name,
          })
        }
        for (const c of costumes.items) {
          map.set(assignmentItemKey('bible_costume', c.public_id), {
            kindLabel: kind('bible.kindCostume'),
            title: c.name,
          })
        }
        map.set(assignmentItemKey('bible_style', 'default'), {
          kindLabel: kind('bible.tab.style'),
          title: kind('bible.tab.style'),
        })
        if (style) {
          const styleTitle = [style.art_direction, style.color_palette].map((s) => s.trim()).find(Boolean)
          if (styleTitle) {
            map.set(assignmentItemKey('bible_style', 'default'), {
              kindLabel: kind('bible.tab.style'),
              title: styleTitle,
            })
          }
        }
        for (const sh of shots.items) {
          const title = sh.slug?.trim() || `E${String(sh.episode_no).padStart(2, '0')}-S${sh.scene_no}-${sh.shot_no}`
          map.set(assignmentItemKey('shot', sh.public_id), {
            kindLabel: kind('team.assignmentKindShot'),
            title,
          })
        }
        setLabels(map)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [productionId, t])

  const resolve = useMemo(() => {
    return (itemType: string, itemPublicId: string): AssignmentItemLabel => {
      const hit = labels.get(assignmentItemKey(itemType, itemPublicId))
      if (hit) return hit
      if (itemType === 'shot_scene') {
        const parsed = parseShotSceneKey(itemPublicId)
        if (parsed) {
          return {
            kindLabel: t('team.assignmentKindScene'),
            title: t('team.assignmentSceneTitle', {
              ep: parsed.episodeNo,
              scene: parsed.sceneNo,
            }),
          }
        }
      }
      return {
        kindLabel: t(`team.assignmentKind.${itemType}`, { defaultValue: itemType }),
        title: itemPublicId,
      }
    }
  }, [labels, t])

  return { labels, loading, resolve }
}
